import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Table from '@decisiv/ui-components/lib/components/Table';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import Button from '@decisiv/ui-components/lib/components/Button';
import Pagination from '@decisiv/ui-components/lib/components/Pagination';
import { ASCENDING, DESCENDING } from 'components/pages/Users/utils/sortUtils';
import Toasts from 'components/Toasts';
import useAccounts from './hooks/useAccounts';
import UsersTable from './components/UsersTable';

const LimitedRoles = ({ csrfToken }) => {
  const {
    getAccounts,
    clearAccounts,
    loading,
    accounts,
    totalPages,
  } = useAccounts(csrfToken);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const runSearch = (
    value,
    nextPage,
    nextSortBy = sortBy,
    nextSortOrder = sortOrder,
  ) => {
    setSearchTerm(value);
    setPage(nextPage);
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    getAccounts(value, nextPage, nextSortBy, nextSortOrder);
  };

  const handleSearch = (value) => {
    setSelectedAccount(null);
    clearAccounts();
    runSearch(value, 1);
  };

  const handleClear = () => {
    setQuery('');
    setSearchTerm('');
    setPage(1);
    setSortBy(null);
    setSortOrder(null);
    setSelectedAccount(null);
    clearAccounts();
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    if (newValue === '') handleClear();
  };

  const handlePageChange = (newPage) => {
    runSearch(searchTerm, newPage);
  };

  const handleSort = (columnName) => (sortingType) => {
    let nextSortBy = null;
    let nextSortOrder = null;
    if (sortingType === ASCENDING) {
      nextSortBy = columnName;
      nextSortOrder = 'ASC';
    } else if (sortingType === DESCENDING) {
      nextSortBy = columnName;
      nextSortOrder = 'DESC';
    }
    runSearch(searchTerm, 1, nextSortBy, nextSortOrder);
  };

  const columns = [
    {
      DataCell: ({ rowData }) => <span>{rowData.decisiv_id}</span>,
      name: 'decisivId',
      title: 'Dealer ID',
      sortBy: handleSort('decisiv_id'),
    },
    {
      DataCell: ({ rowData }) => <span>{rowData.name}</span>,
      name: 'name',
      title: 'Name',
      sortBy: handleSort('name'),
    },
    {
      DataCell: ({ rowData }) => {
        const isSelected = selectedAccount && selectedAccount.id === rowData.id;
        return (
          <Button
            text={isSelected ? 'Selected' : 'Select'}
            size="small"
            variant={isSelected ? 'ghost' : 'primary'}
            onClick={() => setSelectedAccount(rowData)}
          />
        );
      },
      name: 'select',
    },
  ];

  return (
    <>
      <Toasts />
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>Search by Dealer ID or Name</div>
        <div style={{ width: 360 }}>
          <SearchInput
            size="small"
            placeholder="Search accounts"
            value={query}
            onChange={handleChange}
            onSearch={handleSearch}
          />
        </div>
      </div>
      {loading && <span>Loading...</span>}
      {accounts.length > 0 && (
        <>
          <Table columns={columns} data={accounts} />
          {totalPages > 1 && (
            <Flex justifyContent="flex-end" marginTop={2}>
              <Pagination
                activePage={page}
                totalPages={totalPages}
                size="small"
                onPageChange={handlePageChange}
              />
            </Flex>
          )}
        </>
      )}
      {selectedAccount && (
        <UsersTable selectedAccount={selectedAccount} csrfToken={csrfToken} />
      )}
    </>
  );
};

LimitedRoles.propTypes = {
  csrfToken: PropTypes.string.isRequired,
};

export default LimitedRoles;
